import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { injectIntl, intlShape, useIntl } from '../../../util/reactIntl';
import { composeValidators, emailFormatValid, required } from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldPhoneNumberInput } from '../../../components';

import css from './SubscriptionAgencyForm.module.css';
import moment from 'moment';

const SubscriptionAgencyFormComponent = props => {
  const intl = useIntl();
  const [preferredContactDate, setPreferredContactDate] = useState(null);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          disabled,
          handleSubmit,
          formId,
          invalid,
          values,
          form,
          inProgress,
        } = fieldRenderProps;
        const classes = classNames(rootClassName || css.root, className);
        const submitDisabled = invalid || disabled || inProgress;

        // email
        const emailRequiredMessage = intl.formatMessage({
          id: 'SubscriptionAgencyForm.emailRequired',
        });
        const emailRequired = required(emailRequiredMessage);
        const emailInvalidMessage = intl.formatMessage({
          id: 'SubscriptionAgencyForm.emailInvalid',
        });
        const emailValid = emailFormatValid(emailInvalidMessage);

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={intl.formatMessage({
                id: 'SubscriptionAgencyForm.emailLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SubscriptionAgencyForm.emailPlaceholder',
              })}
              validate={composeValidators(emailRequired, emailValid)}
            />

            <FieldTextInput
              className={css.formFld}
              type="text"
              id={formId ? `${formId}.agencyName` : 'agencyName'}
              name="agencyName"
              label={intl.formatMessage({ id: 'SubscriptionAgencyForm.agencyNameLabel' })}
              placeholder={intl.formatMessage({
                id: 'SubscriptionAgencyForm.agencyNamePlaceholder',
              })}
              validate={required(
                intl.formatMessage({ id: 'SubscriptionAgencyForm.budgetRequired' })
              )}
            />
            <FieldTextInput
              className={css.formFld}
              type="text"
              id={formId ? `${formId}.username` : 'username'}
              name="username"
              label={intl.formatMessage({ id: 'SubscriptionAgencyForm.usernameLabel' })}
              placeholder={intl.formatMessage({
                id: 'SubscriptionAgencyForm.usernamePlaceholder',
              })}
              validate={required(
                intl.formatMessage({ id: 'SubscriptionAgencyForm.budgetRequired' })
              )}
            />
            <FieldPhoneNumberInput
              className={css.phone}
              name="phoneNumber"
              id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
              label={intl.formatMessage({ id: 'SubscriptionAgencyForm.phoneLabel' })}
              placeholder={intl.formatMessage({
                id: 'SubscriptionAgencyForm.phonePlaceholder',
              })}
              validate={required(
                intl.formatMessage({
                  id: 'SubscriptionAgencyForm.phoneRequired',
                })
              )}
            />
            <div className={css.datePickerWrapper}>
              <label htmlFor="preferredContactDate">
                {intl.formatMessage({
                  id: 'SubscriptionAgencyForm.preferredContactDateLabel',
                })}
              </label>
              <input
                type="date"
                id="preferredContactDate"
                value={preferredContactDate?.split(' ')[0] || ''}
                onChange={e => {
                  const selectedDate = e.target.value;
                  const newDateTime = `${selectedDate} ${values.preferredContactTime?.split(
                    ' '
                  )[1] || '00:00'}`;
                  setPreferredContactDate(newDateTime);
                  form.change('preferredContactTime', newDateTime);
                }}
              />

              <label htmlFor="preferredContactTime">
                {intl.formatMessage({
                  id: 'SubscriptionAgencyForm.preferredContactTimeLabel',
                })}
              </label>
              <input
                type="time"
                id="preferredContactTime"
                value={preferredContactDate?.split(' ')[1] || ''}
                onChange={e => {
                  const selectedTime = e.target.value;
                  const newDateTime = `${values.preferredContactTime?.split(' ')[0] ||
                    moment().format('YYYY-MM-DD')} ${selectedTime}`;
                  setPreferredContactDate(newDateTime);
                  form.change('preferredContactTime', newDateTime);
                }}
              />
            </div>

            <PrimaryButton
              className={css.submitButton}
              type="submit"
              inProgress={inProgress}
              disabled={submitDisabled}
            >
              {intl.formatMessage({
                id: 'SubscriptionAgencyForm.saveAgencyButton',
              })}
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

SubscriptionAgencyFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
};

const { bool, func, string } = PropTypes;

SubscriptionAgencyFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
};

const SubscriptionAgencyForm = compose(injectIntl)(SubscriptionAgencyFormComponent);
SubscriptionAgencyForm.displayName = 'SubscriptionAgencyForm';

export default SubscriptionAgencyForm;
